import type { AnalyticsFilters } from "../state";

/**
 * Convert AnalyticsFilters to URL search parameters
 * Only includes non-default/non-empty values for cleaner URLs
 */
export const filtersToUrlParams = (
    filters: AnalyticsFilters,
): URLSearchParams => {
    const params = new URLSearchParams();

    // Always include quarter range
    params.set("quarterFrom", filters.quarterFrom);
    params.set("quarterTo", filters.quarterTo);

    // Always include detection limit
    params.set("detectionLimit", filters.detectionLimit.toString());

    // Only include providers if defined
    if (filters.providers !== undefined) {
        if (filters.providers === "all") {
            params.set("providers", "all");
        } else if (filters.providers.length > 0) {
            // Serialize providers as JSON since they're objects
            params.set("providers", JSON.stringify(filters.providers));
        }
    }

    // Only include locationPreset if it has values
    if (filters.locationPreset && filters.locationPreset.length > 0) {
        params.set("locationPreset", filters.locationPreset.join(","));
    }

    // Only include siteType if it has values
    if (filters.siteType && filters.siteType.length > 0) {
        params.set("siteType", filters.siteType.join(","));
    }

    return params;
};

/**
 * Parse URL search parameters to AnalyticsFilters
 * Returns undefined for invalid/missing values (graceful fallback)
 */
export const urlParamsToFilters = (
    urlParams: URLSearchParams,
): Partial<AnalyticsFilters> => {
    const filters: Partial<AnalyticsFilters> = {};

    try {
        // Parse quarterFrom
        const quarterFrom = urlParams.get("quarterFrom");
        if (quarterFrom && isValidQuarter(quarterFrom)) {
            filters.quarterFrom = quarterFrom;
        }

        // Parse quarterTo
        const quarterTo = urlParams.get("quarterTo");
        if (quarterTo && isValidQuarter(quarterTo)) {
            filters.quarterTo = quarterTo;
        }

        // Parse detectionLimit
        const detectionLimit = urlParams.get("detectionLimit");
        if (detectionLimit) {
            const limit = parseFloat(detectionLimit);
            if (!Number.isNaN(limit) && limit >= 0 && limit < 1000000) {
                filters.detectionLimit = limit;
            }
        }

        // Parse providers
        const providers = urlParams.get("providers");
        if (providers) {
            if (providers === "all") {
                filters.providers = "all";
            } else {
                try {
                    // Parse JSON-serialized providers
                    const providerArray = JSON.parse(providers);
                    if (
                        Array.isArray(providerArray) &&
                        providerArray.length > 0
                    ) {
                        filters.providers = providerArray;
                    }
                } catch (e) {
                    console.error("Failed to parse providers JSON:", e);
                }
            }
        }

        // Parse locationPreset
        const locationPreset = urlParams.get("locationPreset");
        if (locationPreset) {
            const presets = locationPreset
                .split(",")
                .map((p) => parseInt(p.trim(), 10))
                .filter((p) => !Number.isNaN(p));
            if (presets.length > 0) {
                filters.locationPreset = presets;
            }
        }

        // Parse siteType
        const siteType = urlParams.get("siteType");
        if (siteType) {
            const types = siteType
                .split(",")
                .map((t) => t.trim())
                .filter(Boolean);
            if (types.length > 0) {
                filters.siteType = types;
            }
        }
    } catch (error) {
        console.error("Error parsing URL parameters:", error);
        return {};
    }

    return filters;
};

/**
 * Validate quarter string format (YYYY-QN)
 */
const isValidQuarter = (quarter: string): boolean => {
    const quarterRegex = /^\d{4}-Q[1-4]$/;
    return quarterRegex.test(quarter);
};
